import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Ban, Search, AlertTriangle } from "lucide-react";

const EMPTY = {
  razon_social: "", nombre_comercial: "", pais: "MX", identificacion_fiscal: "",
  contacto_nombre: "", email: "", telefono: "", moneda_default: "MXN", notas: "",
};

export default function Carriers() {
  const { can } = useAuth();
  const [data, setData] = useState({ items: [], total: 0 });
  const [search, setSearch] = useState("");
  const [activo, setActivo] = useState("all");
  const [pais, setPais] = useState("all");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [similar, setSimilar] = useState([]);
  const [saving, setSaving] = useState(false);

  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [deactivateMsg, setDeactivateMsg] = useState("");
  const limit = 50;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/carriers", {
        params: { search, activo, pais, skip: page * limit, limit },
      });
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [search, activo, pais, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setSimilar([]); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setSimilar([]); setOpen(true); };

  const save = async (force = false) => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/carriers/${editing.id}`, form);
        toast.success("Carrier actualizado");
      } else {
        await api.post(`/carriers?force=${force}`, form);
        toast.success("Carrier creado");
      }
      setOpen(false);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && detail.code === "posible_duplicado") {
        setSimilar(detail.similar || []);
        toast.warning("Se encontraron carriers con nombre parecido");
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally {
      setSaving(false);
    }
  };

  const doDeactivate = async (force = false) => {
    try {
      await api.post(`/carriers/${deactivateTarget.id}/deactivate?force=${force}`);
      toast.success("Carrier desactivado");
      setDeactivateTarget(null); setDeactivateMsg(""); load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && detail.code === "tiene_loads") setDeactivateMsg(detail.message);
      else { toast.error(formatApiErrorDetail(detail)); setDeactivateTarget(null); }
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / limit));

  return (
    <div>
      <PageHeader
        title="Carriers"
        subtitle="Transportistas a los que se les paga"
        actions={
          can("carriers", "crear") && (
            <Button onClick={openCreate} data-testid="new-carrier-btn" className="gap-2">
              <Plus className="h-4 w-4" /> Nuevo carrier
            </Button>
          )
        }
      />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Buscar por código o nombre" value={search}
            onChange={(e) => { setPage(0); setSearch(e.target.value); }} className="pl-9" data-testid="carrier-search" />
        </div>
        <Select value={pais} onValueChange={(v) => { setPage(0); setPais(v); }}>
          <SelectTrigger className="w-full sm:w-36" data-testid="carrier-filter-pais"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los países</SelectItem>
            <SelectItem value="MX">México</SelectItem>
            <SelectItem value="US">Estados Unidos</SelectItem>
          </SelectContent>
        </Select>
        <Select value={activo} onValueChange={(v) => { setPage(0); setActivo(v); }}>
          <SelectTrigger className="w-full sm:w-36" data-testid="carrier-filter-activo"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="true">Activos</SelectItem>
            <SelectItem value="false">Inactivos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-md border border-border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Código</TableHead>
              <TableHead>Razón social</TableHead>
              <TableHead>País</TableHead>
              <TableHead>Moneda</TableHead>
              <TableHead>ID Fiscal</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="text-right">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((c) => (
              <TableRow key={c.id} data-testid={`carrier-row-${c.codigo}`} className={!c.activo ? "opacity-60" : ""}>
                <TableCell className="font-mono text-xs">{c.codigo}</TableCell>
                <TableCell className="font-medium">{c.razon_social}</TableCell>
                <TableCell className="font-mono text-xs">{c.pais}</TableCell>
                <TableCell className="font-mono text-xs">{c.moneda_default}</TableCell>
                <TableCell className="text-muted-foreground">{c.identificacion_fiscal || "—"}</TableCell>
                <TableCell>
                  {c.activo
                    ? <Badge className="bg-green-50 text-green-700 border border-green-200 hover:bg-green-50">Activo</Badge>
                    : <Badge className="bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-100">Inactivo</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {can("carriers", "editar") && (
                      <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-carrier-${c.codigo}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {can("carriers", "desactivar") && c.activo && (
                      <Button variant="ghost" size="icon" onClick={() => setDeactivateTarget(c)} data-testid={`deactivate-carrier-${c.codigo}`}>
                        <Ban className="h-4 w-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {!loading && data.items.length === 0 && (
              <TableRow><TableCell colSpan={7} className="py-10 text-center text-muted-foreground">Sin resultados.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
        <span>{data.total} carrier(s)</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
          <span>Pág. {page + 1} de {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader><SheetTitle className="font-heading">{editing ? "Editar carrier" : "Nuevo carrier"}</SheetTitle></SheetHeader>

          {similar.length > 0 && (
            <div className="my-4 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="carrier-duplicate-warning">
              <div className="flex items-center gap-2 font-medium text-amber-800">
                <AlertTriangle className="h-4 w-4" /> Posibles duplicados
              </div>
              <ul className="mt-2 space-y-1 text-sm text-amber-800">
                {similar.map((s) => (<li key={s.id} className="font-mono text-xs">• {s.codigo} — {s.razon_social}</li>))}
              </ul>
              <Button size="sm" className="mt-3" variant="outline" onClick={() => save(true)} data-testid="carrier-force-create">
                Crear de todos modos
              </Button>
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Razón social *" className="sm:col-span-2">
              <Input value={form.razon_social} onChange={(e) => setForm({ ...form, razon_social: e.target.value })} data-testid="carrier-razon-social" />
            </Field>
            <Field label="Nombre comercial" className="sm:col-span-2">
              <Input value={form.nombre_comercial} onChange={(e) => setForm({ ...form, nombre_comercial: e.target.value })} />
            </Field>
            <Field label="País">
              <Select value={form.pais} onValueChange={(v) => setForm({ ...form, pais: v, moneda_default: v === "MX" ? "MXN" : "USD" })}>
                <SelectTrigger data-testid="carrier-pais"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="MX">México</SelectItem>
                  <SelectItem value="US">Estados Unidos</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Moneda default">
              <Select value={form.moneda_default} onValueChange={(v) => setForm({ ...form, moneda_default: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="MXN">MXN</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="ID Fiscal (RFC / EIN·MC)" className="sm:col-span-2">
              <Input value={form.identificacion_fiscal} onChange={(e) => setForm({ ...form, identificacion_fiscal: e.target.value })} />
            </Field>
            <Field label="Contacto"><Input value={form.contacto_nombre} onChange={(e) => setForm({ ...form, contacto_nombre: e.target.value })} /></Field>
            <Field label="Teléfono"><Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></Field>
            <Field label="Email" className="sm:col-span-2"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Notas" className="sm:col-span-2"><Textarea value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} /></Field>
          </div>

          <SheetFooter className="mt-6">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={() => save(false)} disabled={saving || !form.razon_social} data-testid="carrier-save-btn">
              {saving ? "Guardando..." : "Guardar"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!deactivateTarget} onOpenChange={(o) => { if (!o) { setDeactivateTarget(null); setDeactivateMsg(""); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desactivar carrier</AlertDialogTitle>
            <AlertDialogDescription>
              {deactivateMsg || `¿Desactivar a ${deactivateTarget?.razon_social}?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => doDeactivate(!!deactivateMsg)} data-testid="confirm-deactivate-carrier">
              {deactivateMsg ? "Desactivar de todos modos" : "Desactivar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
